import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import babyHeroImage from "../../assets/images/Hero/baby-photography-guides-hero.webp";
import babyStudioImage from "../../assets/images/baby-bunny-studio-portrait.webp";
import babyStudioImage3 from "../../assets/images/baby-family-neutral-studio-portrait.webp";
import babyStudioImage4 from "../../assets/images/baby-first-birthday-cake-smash-balloons.webp";
import { UiIcon } from "./components/UiIcons";
import { findResourceArticleByHref, formatResourcePublishedAt } from "./data/resourceArticles";
import { ResourceHubSeo } from "./ResourceSeo";
import "./NewbornPages.scss";
import "./BabyArticle.scss";

const babyGuides = [
  {
    eyebrow: "MILESTONES",
    title: "What Is the Best Age for Baby Photos?",
    description: "Compare 3, 6, 9 and 12 months to find the stage you most want to remember.",
    href: "/resources/baby/what-is-the-best-age-for-baby-photos/",
    icon: "03 · 06 · 09 · 12",
    image: babyStudioImage,
    imageAlt: "Baby girl in a lace romper posing with a white bunny",
  },
  {
    eyebrow: "THE EXPERIENCE",
    title: "What Happens During a Baby Photography Session?",
    description: "See how play, breaks, portraits and family connection fit naturally together.",
    href: "/resources/baby/what-happens-during-a-baby-photography-session/",
    icon: "PLAY → PAUSE → PORTRAITS",
    image: babyStudioImage4,
    imageAlt: "Baby girl with a birthday cake and pink balloons",
  },
  {
    eyebrow: "PHOTO DAY",
    title: "How to Prepare for Your Baby Photo Session",
    description: "A simple plan for naps, feeding, outfits and the small things worth bringing.",
    href: "/resources/baby/how-to-prepare-for-your-baby-photo-session/",
    icon: "✓  READY  ✓",
    image: babyStudioImage3,
    imageAlt: "Mother and father holding their baby during a neutral studio portrait",
  },
];

export const BabyHub = (): ReactElement => (
  <main className="newborn-page baby-page">
    <ResourceHubSeo category="baby" />
    <section className="newborn-hub-hero baby-hub-hero">
      <img
        alt="Smiling baby photographed at an expressive milestone"
        className="newborn-hub-hero__background"
        src={babyHeroImage}
      />
      <div className="newborn-shell newborn-hub-hero__content">
        <nav aria-label="Breadcrumb" className="newborn-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/resources/">Resources</Link>
          <span>/</span>
          <span>Baby</span>
        </nav>
        <p className="baby-eyebrow">BABY PHOTOGRAPHY GUIDES</p>
        <h1>Baby Photography</h1>
        <p>
          Helpful guidance for capturing new expressions, growing personalities and the milestones that pass so quickly.
        </p>
      </div>
    </section>

    <section className="newborn-guides-section newborn-shell baby-guides-section">
      <header>
        <div>
          <p className="baby-eyebrow">START HERE</p>
          <h2>Explore baby guides</h2>
        </div>
        <span>From first smiles to first birthdays.</span>
      </header>
      <div className={`baby-guide-grid guide-grid--count-${babyGuides.length}`}>
        {babyGuides.map((guide, index) => {
          const article = findResourceArticleByHref(guide.href);

          return (
            <Link className="baby-guide-card" key={guide.href} to={guide.href}>
              <span className={`baby-guide-card__visual baby-guide-card__visual--${index + 1}`}>
                {guide.image ? <img alt={guide.imageAlt} loading="lazy" src={guide.image} /> : guide.icon}
              </span>
              <span className="baby-guide-card__body">
                <small>{guide.eyebrow}</small>
                <strong>{guide.title}</strong>
                <span>{guide.description}</span>
                {article && (
                  <time className="baby-guide-card__meta" dateTime={article.publishedAt}>
                    {formatResourcePublishedAt(article.publishedAt)} · {article.readTime}
                  </time>
                )}
                <b>
                  Read guide <UiIcon name="arrow" />
                </b>
              </span>
            </Link>
          );
        })}
      </div>
      <section className="newborn-cta baby-cta">
        <div className="newborn-cta__icon">
          <UiIcon name="camera" />
        </div>
        <div>
          <h2>Ready to capture this stage?</h2>
          <p>Tell me your baby&apos;s age and the moments you love right now.</p>
        </div>
        <Link className="newborn-button newborn-button--coral" to="/contact/">
          Plan a session <UiIcon name="arrow" />
        </Link>
      </section>
    </section>
  </main>
);

export default BabyHub;
